// Sync now and Try again (spec C7.3, C16): start a run, then ask every few seconds how it is going
// and reload the list when it finishes.
import { useEffect, useState } from 'react';
import { useRevalidator } from 'react-router';
import type { Api, Result, SyncInfo } from '../auth/api';
import { syncCopy } from './renewal-lines';

/** What pressing Sync now leads to: keep watching, and what the toast says. */
export function startOutcome(res: Result<{ started: true }>): { watch: boolean; text: string } {
  if (res.ok) return { watch: true, text: syncCopy.started };
  if (res.status === 409) return { watch: true, text: syncCopy.running };
  if (res.status === 503) return { watch: false, text: syncCopy.notSetUp };
  return { watch: false, text: syncCopy.failedToStart };
}

/** The toast when a watched run ends, or null while it still runs. */
export function endText(info: SyncInfo): string | null {
  if (info.last?.status === 'running') return null;
  return info.last?.status === 'failed' ? syncCopy.failed(info.last.errorRef) : syncCopy.finished;
}

const pollMs = 5000;

export function useSyncNow(api: Api, sync: SyncInfo) {
  const revalidator = useRevalidator();
  const [syncing, setSyncing] = useState(sync.last?.status === 'running');
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);
  const say = (text: string) => setToast({ id: Date.now(), text });

  useEffect(() => {
    if (!syncing) return;
    const timer = setInterval(async () => {
      try {
        const text = endText(await api.syncStatus());
        if (text === null) return;
        setSyncing(false);
        say(text);
        revalidator.revalidate();
      } catch {
        // A missed check is fine: the next one asks again.
      }
    }, pollMs);
    return () => clearInterval(timer);
  }, [api, syncing, revalidator]);

  async function start() {
    const outcome = startOutcome(await api.startSync());
    if (outcome.watch) setSyncing(true);
    say(outcome.text);
  }

  return { syncing, start, toast };
}
